import React, { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  BarChart3,
  Briefcase,
  Building2,
  Car,
  Check,
  ChevronDown,
  ChevronRight,
  Coins,
  CreditCard,
  Gem,
  Home,
  Landmark,
  Lightbulb,
  Lock,
  Package,
  Plus,
  Save,
  Scale,
  Settings,
  Sparkles,
  Wand2,
  Wrench,
  X,
} from 'lucide-react';
import api from '../../lib/api';
import { money } from '../../lib/money';
import { formatAccountName } from '../../utils/accounts';
import type { Case } from '../../types';
import {
  Button,
  Card,
  ConfirmModal,
  EmptyState,
  IconButton,
  Input,
  Modal,
  Select,
  Skeleton,
  Tag,
  ToolbarChip,
  Tooltip,
} from '../ui';

/* ------------------------------------------------------------------ *
 * The mediation ledger. One shared view two lawyers can look at:
 *   1. a dollar-scaled balance bar (client blue | unplaced | opposing
 *      plum) with the target marker — the page's question, answered
 *      at a glance;
 *   2. a three-lane ledger (client | on the table | opposing) grouped
 *      by asset class, rows as data with controls on hover;
 *   3. divided assets as a single full-width band (one slider each);
 *   4. a "Separate property — not divided" shelf.
 * Party identity uses the system party tokens (client blue / opposing
 * plum) — green stays the money/status color.
 * ------------------------------------------------------------------ */

interface NetValueAsset {
  id: number;
  name: string;
  asset_type: string;
  net_type: string;
  description: string;
  current_value: number;
  manual_value: number;
  statement_balance: number | null;
  statement_balance_as_of: string | null;
  statement_match_lost?: boolean;
  value_source: string;
  split_mode: string;
  split_percent_a: number;
  optimizer_directive: string;
  property_status: string | null;
  placement_basis: string | null;
  debt_balance: number;
  cost_basis: number | null;
  assigned_to: string;
  spouse_label: string | null;
  gross_equity: number;
  embedded_gain: number;
  embedded_tax: number;
  selling_cost: number;
  basis_unknown: boolean;
  net_value: number;
  net_value_range: { best: number; mid: number; worst: number } | null;
  explanation_parts: string[];
  is_financial: boolean;
}

interface Assumptions {
  target_split_percent: number;
  ordinary_income_tax_rate: number;
  cap_gains_tax_rate: number;
  house_selling_cost_percent: number;
  include_early_withdraw_penalty: boolean;
  penalty_rate: number;
  apply_tax_adjustments: boolean;
  primary_residence_exclusion: number;
}

interface AccountBalance {
  bank_label: string;
  balance: number;
  as_of: string | null;
  account_type?: string;
  is_credit_card?: boolean;
}

interface LiabilityRow {
  id: number;
  creditor_name: string;
  liability_type: string | null;
  balance: number;
  property_status: string | null;
  assigned_to: string;
  paired_asset_id?: number | null;
  double_count_risk?: boolean;
}

interface PageWarning {
  kind: string;
  text: string;
}

interface SplitResponse {
  assets: NetValueAsset[];
  separate_assets: NetValueAsset[];
  separate_net: number;
  sp_claimed_net: number;
  warnings: PageWarning[];
  liabilities: LiabilityRow[];
  total_liabilities: number;
  assumptions: Assumptions;
  assets_net_value: number;
  total_net_value: number;
  spouse_a_net: number;
  spouse_b_net: number;
  spouse_a_percent: number;
  spouse_b_percent: number;
  percent_valid: boolean;
  placed_net: number;
  unplaced_net: number;
  equalizing_payment: number;
  equalizing_payer: string | null;
  target_split_percent: number;
  unmatched_account_balances: AccountBalance[];
}

/* ------------------------------------------------------------------ *
 * Party names — the two "sides" are real people. Column headers and
 * sentences use full names, compact buttons use first names, and any
 * case without names falls back to the neutral Side A / Side B.
 * ------------------------------------------------------------------ */
interface PartyNames {
  fullA: string;
  fullB: string;
  shortA: string;
  shortB: string;
}

const DEFAULT_PARTY_NAMES: PartyNames = {
  fullA: 'Side A',
  fullB: 'Side B',
  shortA: 'Side A',
  shortB: 'Side B',
};

const PartyNamesContext = React.createContext<PartyNames>(DEFAULT_PARTY_NAMES);
const usePartyNames = () => React.useContext(PartyNamesContext);

function derivePartyNames(caseData?: Case | null): PartyNames {
  const a = (caseData?.client_name ?? '').trim();
  const b = (caseData?.opposing_party_name ?? '').trim();
  const fullA = a || 'Side A';
  const fullB = b || 'Side B';
  let shortA = a ? a.split(/\s+/)[0] : 'Side A';
  let shortB = b ? b.split(/\s+/)[0] : 'Side B';
  // Same first name (e.g. two Sams): fall back to full names to disambiguate.
  if (a && b && shortA.toLowerCase() === shortB.toLowerCase()) {
    shortA = fullA;
    shortB = fullB;
  }
  return { fullA, fullB, shortA, shortB };
}

const fmt = (v: number) => money(v, { whole: true });

// One decimal, minus-zero clamped: the solver hands back fractional
// percentages (97.3) — rounding them to integers made the labels contradict
// the dollars by four figures.
const fmtPct1 = (v: number) => {
  const clamped = Math.abs(v) < 0.05 ? 0 : v;
  return clamped.toFixed(1);
};

const fmtDate = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

const ASSET_ICONS: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  'Bank Account': Landmark,
  'Bank/Cash': Landmark,
  'Real Estate': Home,
  'Vehicle': Car,
  'Brokerage': BarChart3,
  'Taxable Brokerage': BarChart3,
  'Investment': BarChart3,
  '401k / Traditional Retirement': Building2,
  'Retirement': Building2,
  'Roth Retirement': Coins,
  'Business': Briefcase,
  'Jewelry': Gem,
  'Personal Property': Gem,
  'Other': Package,
};

const AssetIcon = ({ type, size = 16, className = '' }: { type: string; size?: number; className?: string }) => {
  const Icon = ASSET_ICONS[type] ?? Package;
  return <Icon size={size} className={className} />;
};

/* Asset-class grouping: the ledger reads by KIND of money, so lopsided
 * liquidity is visible without a modal. */
type AssetClass = 'liquid' | 'retirement' | 'hard';
const CLASS_ORDER: AssetClass[] = ['liquid', 'retirement', 'hard'];
const CLASS_LABEL: Record<AssetClass, string> = {
  liquid: 'Liquid',
  retirement: 'Retirement',
  hard: 'Hard assets',
};
const classOf = (netType: string): AssetClass => {
  if (netType === 'RETIRE_TRAD' || netType === 'RETIRE_ROTH') return 'retirement';
  if (netType === 'HOUSE' || netType === 'CAR') return 'hard';
  return 'liquid';
};

const HARD_NET_TYPES = ['HOUSE', 'CAR'];
const RETIREMENT_NET_TYPES = ['RETIRE_TRAD', 'RETIRE_ROTH'];
const SPLIT_DIRECTIVES = ['SELL_SPLIT', 'QDRO_SPLIT', 'PERCENT_SPLIT'];

/** Every class divides — the mechanism differs: hard assets sell, retirement
 * goes via QDRO, liquid balances divide directly ("each keeps half the joint
 * checking"). Mirrors allowed_directives on the backend. */
const divideDirectiveFor = (netType: string): 'QDRO_SPLIT' | 'SELL_SPLIT' | 'PERCENT_SPLIT' => {
  if (RETIREMENT_NET_TYPES.includes(netType)) return 'QDRO_SPLIT';
  if (HARD_NET_TYPES.includes(netType)) return 'SELL_SPLIT';
  return 'PERCENT_SPLIT';
};
const divideLabelFor = (netType: string): string =>
  HARD_NET_TYPES.includes(netType) ? 'Sell & split' : 'Split';

/* Characterization badge — Community is the §3.003 default and stays quiet;
 * anything else is a flag the attorney needs to see on the division page. */
function CharacterizationTag({ status }: { status: string | null }) {
  if (!status || status === 'CP_candidate') return null;
  const map: Record<string, { label: string; tone: 'separate' | 'mixed' | 'unknown' }> = {
    SP_candidate: { label: 'Separate', tone: 'separate' },
    SP_suspected: { label: 'Likely separate', tone: 'mixed' },
    Mixed: { label: 'Mixed', tone: 'mixed' },
    Unknown: { label: 'Unclassified', tone: 'unknown' },
  };
  const meta = map[status];
  if (!meta) return null;
  return <Tag size="sm" tone={meta.tone}>{meta.label}</Tag>;
}

/* How did this row get its side? Locked = a human chose; sparkles = the
 * optimizer placed it, with the why kept on the row instead of lost in a
 * closed modal. */
function PlacementGlyph({ asset }: { asset: NetValueAsset }) {
  const names = usePartyNames();
  const directive = asset.optimizer_directive || 'AUTO';
  if (directive === 'KEEP_A' || directive === 'KEEP_B') {
    const who = directive === 'KEEP_A' ? names.shortA : names.shortB;
    return (
      <Tooltip content={`Locked to ${who} — a manual pick; the optimizer never moves it.`}>
        <span className="inline-flex text-ink-faint"><Lock size={12} /></span>
      </Tooltip>
    );
  }
  if (directive === 'AUTO' && (asset.assigned_to === 'SPOUSE_A' || asset.assigned_to === 'SPOUSE_B')) {
    const why =
      asset.placement_basis === 'signal'
        ? 'Placed with its account holder — the balance allowed it.'
        : 'Placed by the optimizer to balance the estate. Still movable.';
    return (
      <Tooltip content={why}>
        <span className="inline-flex text-ink-faint"><Sparkles size={12} /></span>
      </Tooltip>
    );
  }
  return null;
}

/* ------------------------------------------------------------------ *
 * Shared per-asset mutations.
 * ------------------------------------------------------------------ */
function useAssetPatch(caseId: string, assetId: number) {
  const queryClient = useQueryClient();
  const patch = useMutation({
    mutationFn: (updates: Record<string, unknown>) =>
      api.patch(`/cases/${caseId}/assets/${assetId}`, updates),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['net-value-split', caseId] }),
  });
  return { patch, busy: patch.isPending };
}

function useLiabilityMutations(caseId: string, liabilityId: number) {
  const queryClient = useQueryClient();
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['net-value-split', caseId] });
    queryClient.invalidateQueries({ queryKey: ['liabilities', caseId] });
  };
  const patch = useMutation({
    mutationFn: (updates: Record<string, unknown>) =>
      api.patch(`/cases/${caseId}/liabilities/${liabilityId}`, updates),
    onSuccess: invalidate,
  });
  const del = useMutation({
    mutationFn: () => api.delete(`/cases/${caseId}/liabilities/${liabilityId}`),
    onSuccess: invalidate,
  });
  return { patch, del, busy: patch.isPending || del.isPending };
}

/* ============================ BALANCE BAR ============================ */

function BalanceBar({
  data,
  onOpenAssumptions,
  onOpenOptimize,
  caseId,
}: {
  data: SplitResponse;
  onOpenAssumptions: () => void;
  onOpenOptimize: () => void;
  caseId: string;
}) {
  const names = usePartyNames();
  const queryClient = useQueryClient();
  const taxToggle = useMutation({
    mutationFn: (applyTax: boolean) =>
      api.put(`/cases/${caseId}/assumptions`, { apply_tax_adjustments: applyTax }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['net-value-split', caseId] }),
  });

  const netA = data.spouse_a_net;
  const netB = data.spouse_b_net;
  const unplaced = data.unplaced_net;
  const target = data.target_split_percent;
  const payment = data.equalizing_payment;
  const payerShort = data.equalizing_payer === 'SPOUSE_A' ? names.shortA : names.shortB;
  const otherShort = data.equalizing_payer === 'SPOUSE_A' ? names.shortB : names.shortA;
  const applyTax = data.assumptions.apply_tax_adjustments;

  // The bar is dollar-scaled: segments proportional to what each side holds
  // plus a neutral middle for money not yet on either side — the bar cannot
  // claim balance while dollars sit unsorted. Degenerate estates (a side in
  // net debt) skip the bar and speak in dollars.
  const segA = Math.max(netA, 0);
  const segB = Math.max(netB, 0);
  const segU = Math.max(unplaced, 0);
  const segTotal = segA + segB + segU;
  const barDrawable = data.percent_valid && segTotal > 0;
  const wA = barDrawable ? (segA / segTotal) * 100 : 0;
  const wU = barDrawable ? (segU / segTotal) * 100 : 0;

  // De-noising band: a $61 residual on an $854k estate is "even", not a
  // headline. The exact figure stays in the caption.
  const evenBand = Math.max(100, 0.001 * Math.abs(data.placed_net || 0));
  const effectivelyEven = payment < evenBand;

  return (
    <Card className="mb-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="t-eyebrow">Community estate</div>
          <div className="t-num-display text-ink">{fmt(data.total_net_value)}</div>
          <div className="t-caption mt-1">
            {data.total_liabilities > 0 && <>{fmt(data.total_liabilities)} in debts netted out</>}
            {data.separate_net > 0 && (
              <> · {fmt(data.separate_net)} separate property excluded</>
            )}
          </div>
          {data.sp_claimed_net > 0 && (
            <div className="mt-2">
              <Tag size="sm" tone="mixed">
                Includes {fmt(data.sp_claimed_net)} with an unresolved separate-property claim
              </Tag>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1" role="group" aria-label="Valuation basis">
            <ToolbarChip
              selected={applyTax}
              disabled={taxToggle.isPending || applyTax}
              onClick={() => taxToggle.mutate(true)}
              title="Net values discount for taxes, §121, and selling costs"
            >
              Tax-adjusted
            </ToolbarChip>
            <ToolbarChip
              selected={!applyTax}
              disabled={taxToggle.isPending || !applyTax}
              onClick={() => taxToggle.mutate(false)}
              title="Assets at face value (gross equity, no tax) — §1041 transfers are tax-free"
            >
              Face value
            </ToolbarChip>
          </div>
          <Button variant="secondary" size="sm" icon={Settings} onClick={onOpenAssumptions}>
            Assumptions
          </Button>
          <Button size="sm" icon={Wand2} onClick={onOpenOptimize}>
            Optimize split
          </Button>
        </div>
      </div>

      <div className="mt-4">
        <div className="flex items-baseline justify-between gap-3 mb-2">
          <div className="min-w-0">
            <span className="t-card text-party-client">{names.fullA}</span>{' '}
            <span className="t-num text-lg font-semibold text-party-client">{fmt(netA)}</span>
            <span className="t-caption ml-2">
              {data.percent_valid ? `${fmtPct1(data.spouse_a_percent)}%` : '—'}
              {unplaced > 0.005 ? ' of placed' : ''}
            </span>
          </div>
          <div className="min-w-0 text-right">
            <span className="t-caption mr-2">
              {data.percent_valid ? `${fmtPct1(data.spouse_b_percent)}%` : '—'}
              {unplaced > 0.005 ? ' of placed' : ''}
            </span>
            <span className="t-num text-lg font-semibold text-party-opposing">{fmt(netB)}</span>{' '}
            <span className="t-card text-party-opposing">{names.fullB}</span>
          </div>
        </div>

        {barDrawable ? (
          <div
            className="relative h-8 rounded-md overflow-hidden flex"
            role="img"
            aria-label={`Balance: ${names.shortA} ${fmt(netA)}, ${
              segU > 0 ? `${fmt(unplaced)} not yet placed, ` : ''
            }${names.shortB} ${fmt(netB)}; target marker at ${fmtPct1(target)}%`}
          >
            <div className="bg-party-client h-full" style={{ width: `${wA}%` }} />
            {segU > 0 && <div className="bg-cream-dark h-full" style={{ width: `${wU}%` }} />}
            <div className="bg-party-opposing h-full flex-1" />
            <Tooltip content={`Target: ${fmtPct1(target)} / ${fmtPct1(100 - target)}`}>
              <div
                className="absolute inset-y-0 w-0 border-l-2 border-dashed border-cream-light"
                style={{ left: `${target}%` }}
                aria-hidden="true"
              />
            </Tooltip>
          </div>
        ) : (
          <div className="rounded-md bg-cream border border-cream-dark px-4 py-3 text-sm text-ink-soft">
            The sides can't be drawn as shares right now
            {netA < 0 || netB < 0 ? ' — one side holds net debt' : ''}. The dollar totals above
            and the payment below are exact.
          </div>
        )}

        <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm">
            {effectivelyEven ? (
              <span className="text-ink-soft">
                Effectively even{payment >= 1 && <> — {fmt(payment)} from exact</>}
                {target !== 50 && <> · target {fmtPct1(target)}/{fmtPct1(100 - target)}</>}
              </span>
            ) : (
              <span className="font-semibold text-ink">
                {data.equalizing_payer ? `${payerShort} pays ${otherShort} ` : 'Equalizing payment '}
                {fmt(payment)}
                <span className="font-normal text-ink-soft">
                  {' '}to reach {fmtPct1(target)}/{fmtPct1(100 - target)}
                </span>
              </span>
            )}
          </div>
          {unplaced !== 0 && (
            <div className="text-sm font-medium text-clay-600">
              {fmt(Math.abs(unplaced))} not yet on either side
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

/* ============================ ROW DETAILS ============================ */

function AssetDetails({ asset, caseId }: { asset: NetValueAsset; caseId: string }) {
  const { patch, busy } = useAssetPatch(caseId, asset.id);
  const hasStatement = asset.statement_balance !== null;
  const usingStatement = asset.value_source === 'statement';

  return (
    <div className="mt-2 space-y-2 pl-7">
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-ink-faint">
        {asset.current_value !== 0 && <span>Value {fmt(asset.current_value)}</span>}
        {asset.debt_balance > 0 && <span className="text-danger">Debt −{fmt(asset.debt_balance)}</span>}
        {asset.embedded_tax > 0 && <span className="text-clay-600">Tax −{fmt(asset.embedded_tax)}</span>}
        {asset.selling_cost > 0 && (
          <span className="text-clay-600">Sale costs −{fmt(asset.selling_cost)}</span>
        )}
      </div>
      {asset.basis_unknown && asset.net_value_range && (
        <div className="text-xs text-clay-700 bg-clay-50 border border-clay-200 rounded-md px-2 py-1 inline-block">
          Cost basis unknown — true net is {fmt(asset.net_value_range.worst)} to{' '}
          {fmt(asset.net_value_range.best)}; the mid-case {fmt(asset.net_value_range.mid)} is used.
        </div>
      )}
      {hasStatement && (
        <div className="flex items-center gap-2 text-xs text-ink-faint">
          <span>
            Statement {fmtDate(asset.statement_balance_as_of)}: {fmt(asset.statement_balance as number)}
          </span>
          <ToolbarChip
            selected={usingStatement}
            disabled={busy || usingStatement}
            onClick={() => patch.mutate({ value_source: 'statement' })}
          >
            Use statement
          </ToolbarChip>
          <ToolbarChip
            selected={!usingStatement}
            disabled={busy || !usingStatement}
            onClick={() => patch.mutate({ value_source: 'manual' })}
          >
            Use manual ({fmt(asset.manual_value)})
          </ToolbarChip>
        </div>
      )}
      {asset.explanation_parts.length > 0 && (
        <div className="pl-3 border-l-2 border-cream-dark space-y-1">
          <div className="flex items-center gap-1 text-2xs text-ink-faint">
            <Lightbulb size={12} /> How this nets out
          </div>
          {asset.explanation_parts.map((p, i) => (
            <p key={i} className="t-caption">{p}</p>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================ LEDGER ROWS ============================ */

function SideAssetRow({
  asset,
  side,
  caseId,
}: {
  asset: NetValueAsset;
  side: 'a' | 'b';
  caseId: string;
}) {
  const { patch, busy } = useAssetPatch(caseId, asset.id);
  const names = usePartyNames();
  const [open, setOpen] = useState(false);
  const otherLabel = side === 'a' ? names.shortB : names.shortA;
  const otherDirective = side === 'a' ? 'KEEP_B' : 'KEEP_A';
  const otherSide = side === 'a' ? 'SPOUSE_B' : 'SPOUSE_A';
  const divideDirective = divideDirectiveFor(asset.net_type);
  const divideLabel = divideLabelFor(asset.net_type);

  return (
    <div className="group px-3 py-2 border-b border-border-subtle last:border-0">
      <div className="flex items-center gap-2">
        <IconButton
          icon={open ? ChevronDown : ChevronRight}
          size="xs"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-label={`${open ? 'Hide' : 'Show'} details for ${asset.name}`}
          className="flex-shrink-0 text-ink-faint"
        />
        <AssetIcon type={asset.asset_type} size={16} className="text-ink-faint flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-medium text-ink truncate">{asset.name}</span>
            <PlacementGlyph asset={asset} />
            <CharacterizationTag status={asset.property_status} />
            {asset.statement_match_lost && (
              <Tooltip content="This asset tracks a statement, but no statement matches it anymore (renamed or re-split?) — showing the value saved when it was created.">
                <Tag size="sm" tone="warn">Stale value</Tag>
              </Tooltip>
            )}
          </div>
          <div className="t-caption truncate">
            {asset.asset_type}
            {asset.value_source === 'statement' && asset.statement_balance_as_of && (
              <> · statement {fmtDate(asset.statement_balance_as_of)}</>
            )}
            {asset.basis_unknown && <> · basis unknown</>}
          </div>
        </div>
        <div className="flex w-0 items-center gap-1 overflow-hidden opacity-0 group-hover:w-auto group-hover:opacity-100 focus-within:w-auto focus-within:opacity-100">
          <Button
            variant="secondary"
            size="sm"
            icon={ArrowLeftRight}
            disabled={busy}
            onClick={() =>
              patch.mutate({
                optimizer_directive: otherDirective,
                assigned_to: otherSide,
                split_mode: 'assign',
                split_percent_a: null,
              })
            }
          >
            To {otherLabel}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={busy}
            onClick={() =>
              patch.mutate({
                optimizer_directive: divideDirective,
                split_mode: 'percent_split',
                split_percent_a: 50,
                assigned_to: 'UNASSIGNED',
              })
            }
          >
            {divideLabel}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            icon={X}
            disabled={busy}
            title="Unassign — back to the table"
            onClick={() =>
              patch.mutate({
                optimizer_directive: 'AUTO',
                assigned_to: 'UNASSIGNED',
                split_mode: 'assign',
                split_percent_a: null,
              })
            }
          >
            Unassign
          </Button>
        </div>
        <div className={`t-num text-sm font-semibold flex-shrink-0 ${side === 'a' ? 'text-party-client' : 'text-party-opposing'}`}>
          {fmt(asset.net_value)}
        </div>
      </div>
      {open && <AssetDetails asset={asset} caseId={caseId} />}
    </div>
  );
}

function SideLiabilityRow({
  liability,
  side,
  caseId,
  assets,
}: {
  liability: LiabilityRow;
  side: 'a' | 'b';
  caseId: string;
  assets: NetValueAsset[];
}) {
  const { patch, del, busy } = useLiabilityMutations(caseId, liability.id);
  const names = usePartyNames();
  const [open, setOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const otherLabel = side === 'a' ? names.shortB : names.shortA;
  const otherSide = side === 'a' ? 'SPOUSE_B' : 'SPOUSE_A';

  return (
    <div className="group px-3 py-2 border-b border-border-subtle last:border-0">
      <div className="flex items-center gap-2">
        <IconButton
          icon={open ? ChevronDown : ChevronRight}
          size="xs"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-label={`${open ? 'Hide' : 'Show'} details for ${liability.creditor_name}`}
          className="flex-shrink-0 text-ink-faint"
        />
        <CreditCard size={16} className="text-ink-faint flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-medium text-ink truncate">{liability.creditor_name}</span>
            {liability.double_count_risk && (
              <Tooltip content="Paired to an asset that already carries a debt balance — the same loan may be subtracted twice.">
                <Tag size="sm" tone="warn">Possible duplicate</Tag>
              </Tooltip>
            )}
          </div>
          <div className="t-caption truncate">{liability.liability_type || 'Liability'}</div>
        </div>
        <div className="flex w-0 items-center gap-1 overflow-hidden opacity-0 group-hover:w-auto group-hover:opacity-100 focus-within:w-auto focus-within:opacity-100">
          <Button
            variant="secondary"
            size="sm"
            icon={ArrowLeftRight}
            disabled={busy}
            onClick={() => patch.mutate({ assigned_to: otherSide })}
          >
            To {otherLabel}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            icon={X}
            disabled={busy}
            title="Unassign — back to the table"
            onClick={() => patch.mutate({ assigned_to: null })}
          >
            Unassign
          </Button>
          <Button
            variant="danger-ghost"
            size="sm"
            disabled={busy}
            onClick={() => setConfirmDelete(true)}
          >
            Delete
          </Button>
        </div>
        <div className="t-num text-sm font-semibold text-danger flex-shrink-0">
          −{fmt(liability.balance)}
        </div>
      </div>
      {open && (
        <div className="mt-2 pl-7 flex items-center gap-2">
          <span className="t-eyebrow flex-shrink-0">Secured by</span>
          <div className="w-64 min-w-0">
            <Select
              size="sm"
              value={liability.paired_asset_id ?? ''}
              onChange={(e) =>
                patch.mutate({ paired_asset_id: e.target.value ? Number(e.target.value) : null })
              }
              disabled={busy}
              placeholder="— not secured —"
              options={assets.map((a) => ({ value: String(a.id), label: a.name }))}
            />
          </div>
        </div>
      )}
      <ConfirmModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => {
          del.mutate();
          setConfirmDelete(false);
        }}
        title="Delete this debt?"
        message={`${liability.creditor_name} (−${fmt(liability.balance)}) will be removed from the estate. This cannot be undone.`}
        confirmLabel="Delete debt"
        variant="danger"
        loading={del.isPending}
      />
    </div>
  );
}

/* ============================ MIDDLE LANE ============================ */

function TableAssetRow({ asset, caseId }: { asset: NetValueAsset; caseId: string }) {
  const { patch, busy } = useAssetPatch(caseId, asset.id);
  const names = usePartyNames();
  const divideDirective = divideDirectiveFor(asset.net_type);

  return (
    <div className="px-3 py-2 border-b border-border-subtle last:border-0">
      <div className="flex items-center gap-2">
        <AssetIcon type={asset.asset_type} size={16} className="text-ink-faint flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-medium text-ink truncate">{asset.name}</span>
            <CharacterizationTag status={asset.property_status} />
          </div>
          <div className="t-caption truncate">{asset.asset_type}</div>
        </div>
        <div className="t-num text-sm font-semibold text-ink flex-shrink-0">{fmt(asset.net_value)}</div>
      </div>
      <div className="mt-2 flex items-center gap-1 flex-wrap">
        <Button
          size="sm"
          disabled={busy}
          className="bg-party-client hover:bg-party-client"
          onClick={() =>
            patch.mutate({
              optimizer_directive: 'KEEP_A',
              assigned_to: 'SPOUSE_A',
              split_mode: 'assign',
              split_percent_a: null,
            })
          }
        >
          <span className="truncate">{names.shortA}</span>
        </Button>
        <Button
          size="sm"
          disabled={busy}
          className="bg-party-opposing hover:bg-party-opposing"
          onClick={() =>
            patch.mutate({
              optimizer_directive: 'KEEP_B',
              assigned_to: 'SPOUSE_B',
              split_mode: 'assign',
              split_percent_a: null,
            })
          }
        >
          <span className="truncate">{names.shortB}</span>
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={busy}
          onClick={() =>
            patch.mutate({
              optimizer_directive: divideDirective,
              split_mode: 'percent_split',
              split_percent_a: 50,
              assigned_to: 'UNASSIGNED',
            })
          }
        >
          {divideLabelFor(asset.net_type)}
        </Button>
      </div>
    </div>
  );
}

function TableLiabilityRow({ liability, caseId }: { liability: LiabilityRow; caseId: string }) {
  const { patch, del, busy } = useLiabilityMutations(caseId, liability.id);
  const names = usePartyNames();
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <div className="px-3 py-2 border-b border-border-subtle last:border-0">
      <div className="flex items-center gap-2">
        <CreditCard size={16} className="text-ink-faint flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-ink truncate block">{liability.creditor_name}</span>
          <div className="t-caption truncate">{liability.liability_type || 'Liability'}</div>
        </div>
        <div className="t-num text-sm font-semibold text-danger flex-shrink-0">
          −{fmt(liability.balance)}
        </div>
      </div>
      <div className="mt-2 flex items-center gap-1 flex-wrap">
        <Button
          size="sm"
          disabled={busy}
          className="bg-party-client hover:bg-party-client"
          onClick={() => patch.mutate({ assigned_to: 'SPOUSE_A' })}
        >
          <span className="truncate">{names.shortA}</span>
        </Button>
        <Button
          size="sm"
          disabled={busy}
          className="bg-party-opposing hover:bg-party-opposing"
          onClick={() => patch.mutate({ assigned_to: 'SPOUSE_B' })}
        >
          <span className="truncate">{names.shortB}</span>
        </Button>
        <Button variant="danger-ghost" size="sm" disabled={busy} onClick={() => setConfirmDelete(true)}>
          Delete
        </Button>
      </div>
      <ConfirmModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => {
          del.mutate();
          setConfirmDelete(false);
        }}
        title="Delete this debt?"
        message={`${liability.creditor_name} (−${fmt(liability.balance)}) will be removed from the estate. This cannot be undone.`}
        confirmLabel="Delete debt"
        variant="danger"
        loading={del.isPending}
      />
    </div>
  );
}

/** Unmatched statement account: place it in ONE click. Becoming an "asset"
 * (the estate ledger row that carries characterization, values, and the I&A
 * citation trail) happens as a side effect of the decision — Sherri / Jack /
 * Split — instead of a separate promotion chore the user has to know about. */
function TableCreateRow({ account, caseId }: { account: AccountBalance; caseId: string }) {
  const queryClient = useQueryClient();
  const names = usePartyNames();
  const isCreditCard = !!account.is_credit_card;
  // A negative credit-card balance is a CREDIT — money owed TO the holder.
  // Creating it as a debt would swing the estate by twice the amount.
  const isCreditBalance = isCreditCard && (account.balance || 0) < 0;
  const isDebt = isCreditCard && !isCreditBalance;
  const magnitude = Math.abs(account.balance || 0);

  const createMutation = useMutation({
    mutationFn: async (opts: { assign?: 'SPOUSE_A' | 'SPOUSE_B'; split?: boolean }) => {
      if (isDebt) {
        await api.post(`/cases/${caseId}/liabilities`, {
          creditor_name: account.bank_label,
          balance: magnitude,
          liability_type: 'Credit Card',
          property_status: 'CP_candidate',
          assigned_to: opts.assign ?? null,
          notes: `Created from statement (as of ${account.as_of ?? 'unknown'}).`,
        });
        return;
      }
      // Creating with a side derives the manual KEEP directive server-side.
      const { data: created } = await api.post(`/cases/${caseId}/assets`, {
        asset_type: 'Bank Account',
        name: isCreditBalance ? `${account.bank_label} (credit balance)` : account.bank_label,
        estimated_value: isCreditBalance ? magnitude : account.balance,
        property_status: 'CP_candidate',
        value_source: isCreditBalance ? 'manual' : 'statement',
        assigned_to: opts.assign ?? null,
      });
      if (opts.split && created?.id) {
        await api.patch(`/cases/${caseId}/assets/${created.id}`, {
          optimizer_directive: 'PERCENT_SPLIT',
          split_mode: 'percent_split',
          split_percent_a: 50,
          assigned_to: 'UNASSIGNED',
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['net-value-split', caseId] });
      queryClient.invalidateQueries({ queryKey: ['assets', caseId] });
      queryClient.invalidateQueries({ queryKey: ['liabilities', caseId] });
    },
  });

  // Stay disabled after a successful create until the refetch removes this row,
  // so a quick double-click can't create a duplicate.
  const busy = createMutation.isPending || createMutation.isSuccess;

  return (
    <div className="px-3 py-2 border-b border-border-subtle last:border-0">
      <div className="flex items-center gap-2">
        {isDebt ? (
          <CreditCard size={16} className="text-ink-faint flex-shrink-0" />
        ) : (
          <Landmark size={16} className="text-ink-faint flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-ink truncate block">
            {formatAccountName(account.bank_label)}
          </span>
          <div className="t-caption truncate">
            {isCreditBalance
              ? 'Credit balance — owed to the holder'
              : isCreditCard
              ? 'Statement · credit card'
              : 'Statement account'}
            {account.as_of ? ` · ${fmtDate(account.as_of)}` : ''}
          </div>
        </div>
        <div className={`t-num text-sm font-semibold flex-shrink-0 ${isDebt ? 'text-danger' : 'text-ink'}`}>
          {isDebt ? `−${fmt(magnitude)}` : fmt(magnitude)}
        </div>
      </div>
      <div className="mt-2 flex items-center gap-1 flex-wrap">
        <Button
          size="sm"
          disabled={busy}
          className="bg-party-client hover:bg-party-client"
          onClick={() => createMutation.mutate({ assign: 'SPOUSE_A' })}
        >
          <span className="truncate">{names.shortA}</span>
        </Button>
        <Button
          size="sm"
          disabled={busy}
          className="bg-party-opposing hover:bg-party-opposing"
          onClick={() => createMutation.mutate({ assign: 'SPOUSE_B' })}
        >
          <span className="truncate">{names.shortB}</span>
        </Button>
        {!isDebt && (
          <Button
            variant="secondary"
            size="sm"
            disabled={busy}
            onClick={() => createMutation.mutate({ split: true })}
          >
            Split
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          icon={Plus}
          disabled={busy}
          onClick={() => createMutation.mutate({})}
          className="text-xs font-normal text-ink-faint"
        >
          Add only
        </Button>
      </div>
    </div>
  );
}

/* ============================ DIVIDED BAND ============================ */

/** One divided (QDRO / sell-split) asset: a SINGLE slider — the old design
 * rendered the same control twice, once per column, with live desyncs. */
function DividedAssetRow({ asset, caseId }: { asset: NetValueAsset; caseId: string }) {
  const { patch, busy } = useAssetPatch(caseId, asset.id);
  const names = usePartyNames();
  const [sliderVal, setSliderVal] = useState(asset.split_percent_a);
  const [customInput, setCustomInput] = useState<string>(fmtPct1(asset.split_percent_a));
  const [serverVal, setServerVal] = useState(asset.split_percent_a);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Server told us a new percentage (e.g. the optimizer re-tuned it):
  // adopt it during render — the sanctioned prop→state adjustment pattern.
  if (serverVal !== asset.split_percent_a) {
    setServerVal(asset.split_percent_a);
    setSliderVal(asset.split_percent_a);
    setCustomInput(fmtPct1(asset.split_percent_a));
  }

  const pctA = sliderVal;
  const pctB = 100 - sliderVal;
  const shareA = (asset.net_value * pctA) / 100;
  const shareB = (asset.net_value * pctB) / 100;
  const isRealEstate = asset.asset_type === 'Real Estate';
  // Preserve which KIND of split this is when the user nudges the percentage.
  const splitDirective = SPLIT_DIRECTIVES.includes(asset.optimizer_directive)
    ? asset.optimizer_directive
    : divideDirectiveFor(asset.net_type);
  const splitVerb =
    splitDirective === 'QDRO_SPLIT'
      ? 'Divided penalty-free (QDRO / transfer incident to divorce)'
      : splitDirective === 'PERCENT_SPLIT'
      ? 'Balance divided directly — each party takes their share'
      : isRealEstate
      ? 'Sold — proceeds split'
      : 'Sold and split';

  // One decimal of precision end-to-end: the solver resolves fractional
  // percentages (97.3) and the control must be able to represent them —
  // an integer-only input silently moved ~$1,000 on a $360k account.
  const commitPercent = (val: number) => {
    const clamped = Math.max(1, Math.min(99, Math.round(val * 10) / 10));
    setSliderVal(clamped);
    setCustomInput(fmtPct1(clamped));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      patch.mutate({
        optimizer_directive: splitDirective,
        split_mode: 'percent_split',
        split_percent_a: clamped,
      });
    }, 250);
  };

  const handleCustomCommit = () => {
    const parsed = parseFloat(customInput);
    if (!Number.isFinite(parsed)) {
      setCustomInput(fmtPct1(sliderVal));
      return;
    }
    commitPercent(parsed);
  };

  return (
    <div className="px-3 py-3 border-b border-border-subtle last:border-0">
      <div className="flex items-center gap-2 flex-wrap">
        <AssetIcon type={asset.asset_type} size={16} className="text-ink-faint flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-medium text-ink truncate">{asset.name}</span>
            <CharacterizationTag status={asset.property_status} />
          </div>
          <div className="t-caption truncate">{splitVerb}</div>
        </div>
        <div className="t-num text-sm font-semibold text-ink flex-shrink-0">{fmt(asset.net_value)}</div>
      </div>

      {isRealEstate && (asset.selling_cost > 0 || asset.debt_balance > 0 || asset.embedded_tax > 0) && (
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-ink-faint pl-7">
          <span>Gross {fmt(asset.current_value)}</span>
          {asset.selling_cost > 0 && <span className="text-clay-600">Sale costs −{fmt(asset.selling_cost)}</span>}
          {asset.debt_balance > 0 && <span className="text-danger">Payoff −{fmt(asset.debt_balance)}</span>}
          {asset.embedded_tax > 0 && <span className="text-clay-600">Tax −{fmt(asset.embedded_tax)}</span>}
          <span className="font-medium text-ink">Net to split {fmt(asset.net_value)}</span>
        </div>
      )}

      <div className="mt-3 pl-7">
        <div className="flex justify-between text-xs font-semibold mb-1">
          <span className="text-party-client">
            {names.shortA}: {fmt(shareA)} ({fmtPct1(pctA)}%)
          </span>
          <span className="text-party-opposing">
            {names.shortB}: {fmt(shareB)} ({fmtPct1(pctB)}%)
          </span>
        </div>
        {/* eslint-disable-next-line no-restricted-syntax -- native range slider: no ui/Slider primitive exists yet; thumb styled by .range-split */}
        <input
          type="range"
          min={1}
          max={99}
          step={0.1}
          value={sliderVal}
          onChange={(e) => {
            const val = Number(e.target.value);
            setSliderVal(val);
            setCustomInput(fmtPct1(val));
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => {
              patch.mutate({
                optimizer_directive: splitDirective,
                split_mode: 'percent_split',
                split_percent_a: Math.round(val * 10) / 10,
              });
            }, 350);
          }}
          aria-label={`Share of ${asset.name} to ${names.shortA}, percent`}
          className="range-split w-full"
          style={{
            background: `linear-gradient(to right, var(--color-party-client) 0%, var(--color-party-client) ${sliderVal}%, var(--color-party-opposing) ${sliderVal}%, var(--color-party-opposing) 100%)`,
          }}
        />
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <ToolbarChip
            selected={Math.abs(sliderVal - 50) < 0.05}
            disabled={busy || Math.abs(sliderVal - 50) < 0.05}
            onClick={() => commitPercent(50)}
          >
            50 / 50
          </ToolbarChip>
          <div className="flex items-center gap-1 ml-auto">
            <Input
              size="sm"
              type="number"
              min={1}
              max={99}
              step={0.1}
              value={customInput}
              onChange={(e) => setCustomInput(e.target.value)}
              onBlur={handleCustomCommit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
              }}
              disabled={busy}
              aria-label={`Percent of ${asset.name} to ${names.shortA}`}
              className="w-20 text-center text-xs font-semibold"
            />
            <span className="text-xs text-ink-faint">% to {names.shortA}</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              patch.mutate({
                optimizer_directive: 'AUTO',
                split_mode: 'assign',
                split_percent_a: null,
                assigned_to: 'UNASSIGNED',
              })
            }
            disabled={busy}
            className="text-xs font-normal text-ink-faint"
          >
            Cancel split
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ============================ LANES ============================ */

function ClassGroup({
  label,
  subtotal,
  children,
}: {
  label: string;
  subtotal: number;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center justify-between px-3 py-1 bg-surface-well border-y border-border-subtle">
        <span className="t-eyebrow">{label}</span>
        <span className="t-num text-xs text-ink-soft">{fmt(subtotal)}</span>
      </div>
      {children}
    </div>
  );
}

function SideLane({
  side,
  assetsByClass,
  liabs,
  allAssets,
  caseId,
}: {
  side: 'a' | 'b';
  assetsByClass: Record<AssetClass, NetValueAsset[]>;
  liabs: LiabilityRow[];
  allAssets: NetValueAsset[];
  caseId: string;
}) {
  const names = usePartyNames();
  const label = side === 'a' ? names.fullA : names.fullB;
  const isEmpty =
    CLASS_ORDER.every((c) => assetsByClass[c].length === 0) && liabs.length === 0;

  return (
    <section
      aria-label={`${label} column`}
      className={`rounded-xl border border-border-subtle bg-cream-light overflow-hidden border-t-4 ${
        side === 'a' ? 'border-t-party-client' : 'border-t-party-opposing'
      }`}
    >
      {isEmpty ? (
        <div className="px-4 py-8 text-center t-caption">Nothing on this side yet.</div>
      ) : (
        <>
          {CLASS_ORDER.map((cls) => {
            const rows = assetsByClass[cls];
            if (rows.length === 0) return null;
            const subtotal = rows.reduce((s, a) => s + a.net_value, 0);
            return (
              <ClassGroup key={cls} label={CLASS_LABEL[cls]} subtotal={subtotal}>
                {rows.map((a) => (
                  <SideAssetRow key={a.id} asset={a} side={side} caseId={caseId} />
                ))}
              </ClassGroup>
            );
          })}
          {liabs.length > 0 && (
            <ClassGroup label="Debts" subtotal={-liabs.reduce((s, l) => s + l.balance, 0)}>
              {liabs.map((l) => (
                <SideLiabilityRow
                  key={l.id}
                  liability={l}
                  side={side}
                  caseId={caseId}
                  assets={allAssets}
                />
              ))}
            </ClassGroup>
          )}
        </>
      )}
    </section>
  );
}

/* ============================ ASSUMPTIONS MODAL ============================ */

/** Percent fields are edited as STRINGS so a decimal rate — the real 23.8%
 * cap-gains+NIIT — is typeable; the old inputs re-rounded on every keystroke. */
function AssumptionsModal({
  caseId,
  assumptions,
  onClose,
}: {
  caseId: string;
  assumptions: Assumptions;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const names = usePartyNames();
  const [form, setForm] = useState(() => ({
    target_split_percent: String(assumptions.target_split_percent),
    ordinary_income_tax_rate: (assumptions.ordinary_income_tax_rate * 100).toString(),
    cap_gains_tax_rate: (assumptions.cap_gains_tax_rate * 100).toString(),
    house_selling_cost_percent: (assumptions.house_selling_cost_percent * 100).toString(),
    penalty_rate: (assumptions.penalty_rate * 100).toString(),
    primary_residence_exclusion: String(assumptions.primary_residence_exclusion),
    include_early_withdraw_penalty: assumptions.include_early_withdraw_penalty,
    apply_tax_adjustments: assumptions.apply_tax_adjustments,
  }));

  const num = (s: string, fallback: number) => {
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : fallback;
  };

  const saveMutation = useMutation({
    mutationFn: () =>
      api.put(`/cases/${caseId}/assumptions`, {
        target_split_percent: Math.max(1, Math.min(99, num(form.target_split_percent, 50))),
        ordinary_income_tax_rate: num(form.ordinary_income_tax_rate, 24) / 100,
        cap_gains_tax_rate: num(form.cap_gains_tax_rate, 15) / 100,
        house_selling_cost_percent: num(form.house_selling_cost_percent, 6) / 100,
        penalty_rate: num(form.penalty_rate, 10) / 100,
        primary_residence_exclusion: num(form.primary_residence_exclusion, 500000),
        include_early_withdraw_penalty: form.include_early_withdraw_penalty,
        apply_tax_adjustments: form.apply_tax_adjustments,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['net-value-split', caseId] });
      onClose();
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Tax & Split Assumptions"
      size="lg"
      footer={
        <>
          <Button size="sm" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button size="sm" icon={Save} onClick={() => saveMutation.mutate()} loading={saveMutation.isPending}>
            Save
          </Button>
        </>
      }
    >
      <label className="flex items-start gap-3 mb-4 p-3 rounded-lg bg-clay-50 border border-clay-200 cursor-pointer">
        {/* eslint-disable-next-line no-restricted-syntax -- native checkbox: no ui/Checkbox primitive exists yet */}
        <input
          type="checkbox"
          checked={form.apply_tax_adjustments}
          onChange={(e) => setForm({ ...form, apply_tax_adjustments: e.target.checked })}
          className="mt-1 h-4 w-4 accent-green-700"
        />
        <div className="text-sm">
          <div className="font-semibold text-clay-900">Apply tax implications</div>
          <div className="text-xs text-clay-600 leading-relaxed">
            On: net values discount for income/capital-gains tax, the home §121 exclusion, and
            selling costs. Off: assets show at <strong>face value</strong> (gross equity, no tax) —
            dividing property between spouses is itself tax-free (§1041) and many courts divide pre-tax.
          </div>
        </div>
      </label>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Input
          label={`Target split — ${names.shortA} (%)`}
          type="number"
          min={1}
          max={99}
          step={1}
          value={form.target_split_percent}
          onChange={(e) => setForm({ ...form, target_split_percent: e.target.value })}
        />
        {form.apply_tax_adjustments && (
          <>
            <Input
              label="Income tax rate (%)"
              type="number"
              step={0.1}
              value={form.ordinary_income_tax_rate}
              onChange={(e) => setForm({ ...form, ordinary_income_tax_rate: e.target.value })}
            />
            <Input
              label="Cap gains rate (%)"
              type="number"
              step={0.1}
              value={form.cap_gains_tax_rate}
              onChange={(e) => setForm({ ...form, cap_gains_tax_rate: e.target.value })}
            />
            <Input
              label="House selling cost (%)"
              type="number"
              step={0.1}
              value={form.house_selling_cost_percent}
              onChange={(e) => setForm({ ...form, house_selling_cost_percent: e.target.value })}
            />
            <Input
              label="Home gain exclusion §121 ($)"
              type="number"
              step={1000}
              value={form.primary_residence_exclusion}
              onChange={(e) => setForm({ ...form, primary_residence_exclusion: e.target.value })}
            />
            <div className="flex items-center gap-2 pt-5">
              {/* eslint-disable-next-line no-restricted-syntax -- native checkbox: no ui/Checkbox primitive exists yet */}
              <input
                type="checkbox"
                id="penalty"
                checked={form.include_early_withdraw_penalty}
                onChange={(e) => setForm({ ...form, include_early_withdraw_penalty: e.target.checked })}
                className="h-4 w-4 accent-green-700"
              />
              <label htmlFor="penalty" className="text-sm text-ink-soft">Early withdrawal penalty</label>
            </div>
            {form.include_early_withdraw_penalty && (
              <Input
                label="Penalty rate (%)"
                type="number"
                step={0.1}
                value={form.penalty_rate}
                onChange={(e) => setForm({ ...form, penalty_rate: e.target.value })}
              />
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

/* ============================ OPTIMIZE MODAL ============================ */

function extractErrorMessage(e: unknown, fallback: string): string {
  if (typeof e === 'object' && e !== null) {
    const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
    const detail = err.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (typeof err.message === 'string') return err.message;
  }
  return fallback;
}

interface AISuggestion {
  asset_id: number;
  name: string;
  asset_type: string;
  net_value: number;
  current_directive: string;
  suggested_directive: string;
  suggested_split_percent_a?: number | null;
  resolved_side: string;
  placement_basis?: string | null;
  recommend_sell?: boolean;
  changed: boolean;
  rationale: string;
  holder_names?: string[];
  entity_holder?: string | null;
  if_kept_home_payment?: number | null;
}

interface SideComposition {
  liquid_net: number;
  retirement_net: number;
  hard_net: number;
  net_total: number;
  face_total: number;
}

interface AIOptimizeExplanation {
  narrative: { kind: string; text: string }[];
  composition: { a: SideComposition; b: SideComposition };
  one_sided_discovery: boolean;
}

interface PreviewLiabilityPlacement {
  liability_id: number;
  name: string | null;
  balance: number | null;
  side: string;
}

interface AIOptimizePreview {
  target_a_percent: number;
  preview_a_net: number;
  preview_b_net: number;
  preview_a_percent: number;
  preview_b_percent: number;
  percent_valid?: boolean;
  total_net_value: number;
  unplaced_net?: number;
  gap_to_target?: number;
  equalizing_payment?: number;
  equalizing_payer?: string | null;
  suggestions: AISuggestion[];
  liability_placements?: PreviewLiabilityPlacement[];
  separate_assets_count?: number;
  separate_net?: number;
  explanation?: AIOptimizeExplanation;
  fallback_used: boolean;
  ai_cost_usd: number;
}

function directiveLabel(key: string, names: PartyNames): string {
  switch (key) {
    case 'KEEP_A':
      return `Keep · ${names.shortA}`;
    case 'KEEP_B':
      return `Keep · ${names.shortB}`;
    case 'SELL_SPLIT':
      return 'Sell & split';
    case 'QDRO_SPLIT':
      return 'Divide (penalty-free)';
    case 'PERCENT_SPLIT':
      return 'Split';
    case 'AUTO':
      return 'Auto';
    default:
      return key;
  }
}

function directiveBadge(s: AISuggestion, names: PartyNames): { text: string; cls: string } {
  if (SPLIT_DIRECTIVES.includes(s.suggested_directive)) {
    const pa = s.suggested_split_percent_a ?? 50;
    const verb =
      s.suggested_directive === 'SELL_SPLIT'
        ? 'Sell & split'
        : s.suggested_directive === 'QDRO_SPLIT'
        ? 'Divide'
        : 'Split';
    return { text: `${verb} ${fmtPct1(pa)}/${fmtPct1(100 - pa)}`, cls: 'bg-clay-50 text-clay-700' };
  }
  if (s.suggested_directive === 'KEEP_A' || s.resolved_side === 'SPOUSE_A') {
    return {
      text: s.suggested_directive === 'AUTO' ? `Auto → ${names.shortA}` : `Keep · ${names.shortA}`,
      cls: 'bg-party-client-soft text-party-client',
    };
  }
  if (s.suggested_directive === 'KEEP_B' || s.resolved_side === 'SPOUSE_B') {
    return {
      text: s.suggested_directive === 'AUTO' ? `Auto → ${names.shortB}` : `Keep · ${names.shortB}`,
      cls: 'bg-party-opposing-soft text-party-opposing',
    };
  }
  return { text: 'Unassigned', cls: 'bg-cream-dark text-ink-soft' };
}

const PRESET_TARGETS = [40, 45, 50, 55, 60];

/** One side's package: a stacked liquid/retirement/hard bar plus face-value
 * caption. Assets only — debts land in the side totals, and the block says so. */
function CompositionRow({ label, comp }: { label: string; comp: SideComposition }) {
  const total = comp.net_total || 1;
  const segs = [
    { key: 'Liquid', value: comp.liquid_net, color: 'var(--color-green-500)' },
    { key: 'Retirement', value: comp.retirement_net, color: 'var(--color-clay-400)' },
    { key: 'Hard assets', value: comp.hard_net, color: 'var(--color-green-900)' },
  ].filter((s) => s.value > 0);
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-24 flex-shrink-0 font-semibold text-ink-soft truncate" title={label}>{label}</span>
      <div className="flex-1 h-3 rounded-full overflow-hidden bg-cream-dark flex">
        {segs.map((s) => (
          <div
            key={s.key}
            style={{ width: `${(s.value / total) * 100}%`, backgroundColor: s.color }}
            title={`${s.key}: ${fmt(s.value)} (${((s.value / total) * 100).toFixed(0)}%)`}
          />
        ))}
      </div>
      <span className="w-40 flex-shrink-0 text-right tabular-nums text-ink-faint">
        {fmt(comp.net_total)} <span className="opacity-70">· face {fmt(comp.face_total)}</span>
      </span>
    </div>
  );
}

function HowItDecided({ explanation }: { explanation: AIOptimizeExplanation }) {
  const names = usePartyNames();
  const steps = explanation.narrative.filter((n) => n.kind !== 'warning');
  const warnings = explanation.narrative.filter((n) => n.kind === 'warning');
  return (
    <div className="mb-4 rounded-xl border border-cream-dark bg-cream p-3">
      <div className="flex items-center gap-2 t-eyebrow text-green-700 mb-2">
        <Lightbulb size={12} /> How it decided
      </div>
      <ul className="space-y-2 mb-3">
        {steps.map((n, i) => (
          <li key={i} className="text-xs text-ink-soft leading-relaxed pl-3 border-l-2 border-cream-dark">
            {n.text}
          </li>
        ))}
      </ul>
      <div className="space-y-2 mb-1">
        <div className="t-eyebrow">Asset mix per side (assets only — debts land in the totals above)</div>
        <CompositionRow label={names.shortA} comp={explanation.composition.a} />
        <CompositionRow label={names.shortB} comp={explanation.composition.b} />
        <div className="flex items-center gap-3 text-2xs text-ink-faint pl-24 ml-2">
          <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--color-green-500)' }} /> Liquid</span>
          <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--color-clay-400)' }} /> Retirement</span>
          <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--color-green-900)' }} /> Hard assets</span>
        </div>
      </div>
      {warnings.map((n, i) => (
        <div key={i} className="mt-2 px-3 py-2 rounded-lg bg-clay-50 border border-clay-200 text-xs text-clay-700 leading-relaxed">
          {n.text}
        </div>
      ))}
    </div>
  );
}

function AIOptimizeModal({
  caseId,
  defaultTarget,
  onClose,
}: {
  caseId: string;
  defaultTarget: number;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const names = usePartyNames();
  // Seeded from the case assumptions: the page and the optimizer share ONE
  // target. (The old modal hardcoded 50 and never wrote the choice back.)
  const roundedDefault = Math.round(defaultTarget);
  const [target, setTarget] = useState<number>(
    PRESET_TARGETS.includes(roundedDefault) ? roundedDefault : 50,
  );
  const [customTarget, setCustomTarget] = useState<string>(
    PRESET_TARGETS.includes(roundedDefault) ? '' : String(defaultTarget),
  );
  // Default ON: re-running the optimizer must not silently discard the sides
  // the user already picked by hand.
  const [respectManual, setRespectManual] = useState<boolean>(true);
  const [preview, setPreview] = useState<AIOptimizePreview | null>(null);
  const [error, setError] = useState<string>('');

  const resolvedTarget = (() => {
    if (customTarget.trim() !== '') {
      const n = parseFloat(customTarget);
      if (!isNaN(n) && n > 0 && n < 100) return n;
    }
    return target;
  })();

  const previewMutation = useMutation({
    mutationFn: async (opts?: { forceGreedy?: boolean }) => {
      setError('');
      const { data } = await api.post(`/cases/${caseId}/ai-optimize-split`, {
        target_a_percent: resolvedTarget,
        respect_manual: respectManual,
        force_greedy: !!opts?.forceGreedy,
      });
      return data as AIOptimizePreview;
    },
    onSuccess: (data) => setPreview(data),
    onError: (e: unknown) => {
      setError(extractErrorMessage(e, 'AI optimize failed.'));
    },
  });

  const liabilityCount = preview?.liability_placements?.length ?? 0;
  const changedCount = preview?.suggestions.filter((s) => s.changed).length ?? 0;
  const applyCount = changedCount + liabilityCount;

  const applyMutation = useMutation({
    mutationFn: async () => {
      if (!preview) return;
      const assignments = preview.suggestions
        .filter((s) => s.changed)
        .map((s) => ({
          asset_id: s.asset_id,
          optimizer_directive: s.suggested_directive,
          // For AUTO the solver-resolved side is what gets persisted.
          assigned_to: s.resolved_side,
          // Every fractional directive needs its percentage — the backend
          // 422s a split without one.
          split_percent_a: SPLIT_DIRECTIVES.includes(s.suggested_directive)
            ? s.suggested_split_percent_a ?? 50
            : null,
        }));
      // The plan is applied WHOLE: asset moves, the debt placements the
      // preview priced, and the target it was solved for — so the page after
      // Apply shows the same numbers the user just approved.
      await api.post(`/cases/${caseId}/ai-optimize-split/apply`, {
        assignments,
        liabilities: (preview.liability_placements ?? []).map((l) => ({
          liability_id: l.liability_id,
          assigned_to: l.side,
        })),
        target_a_percent: preview.target_a_percent,
        respect_manual: respectManual,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['net-value-split', caseId] });
      queryClient.invalidateQueries({ queryKey: ['liabilities', caseId] });
      onClose();
    },
    onError: (e: unknown) => {
      setError(extractErrorMessage(e, 'Apply failed.'));
    },
  });

  const gapRaw = preview?.gap_to_target;
  const gap = typeof gapRaw === 'number' ? (Math.abs(gapRaw) < 0.05 ? 0 : gapRaw) : null;

  return (
    <Modal
      open
      onClose={onClose}
      title="Optimize Split"
      size="xl"
      footer={
        <>
          {!preview && (
            <>
              <Button size="sm" variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                size="sm"
                icon={Wand2}
                onClick={() => previewMutation.mutate(undefined)}
                loading={previewMutation.isPending}
              >
                Generate plan
              </Button>
            </>
          )}
          {preview && (
            <>
              <Button size="sm" variant="secondary" icon={ArrowLeft} onClick={() => setPreview(null)}>
                Back
              </Button>
              {applyCount === 0 ? (
                <Button size="sm" icon={Check} onClick={onClose}>
                  Close — nothing to change
                </Button>
              ) : (
                <Button size="sm" onClick={() => applyMutation.mutate()} loading={applyMutation.isPending}>
                  Apply ({applyCount})
                </Button>
              )}
            </>
          )}
        </>
      }
    >
      <div>
        {!preview && (
          <>
            <div className="mb-4">
              <div className="t-eyebrow mb-2">
                Target ratio ({names.shortA} / {names.shortB})
              </div>
              <div className="flex flex-wrap gap-2 mb-2">
                {PRESET_TARGETS.map((t) => (
                  <ToolbarChip
                    key={t}
                    selected={customTarget === '' && target === t}
                    onClick={() => {
                      setTarget(t);
                      setCustomTarget('');
                    }}
                  >
                    {t}/{100 - t}
                  </ToolbarChip>
                ))}
                <div className="flex items-center gap-1 ml-1">
                  <Input
                    type="number"
                    value={customTarget}
                    onChange={(e) => setCustomTarget(e.target.value)}
                    placeholder="Custom"
                    className="w-20 text-sm"
                    min={1}
                    max={99}
                  />
                  <span className="text-xs text-ink-faint">% to {names.shortA}</span>
                </div>
              </div>
              <div className="text-xs text-ink-faint">
                Effective target: <strong>{fmtPct1(resolvedTarget)}%</strong> to {names.shortA},{' '}
                <strong>{fmtPct1(100 - resolvedTarget)}%</strong> to {names.shortB}. Applying the
                plan makes this the page's target too.
              </div>
            </div>

            <label className="flex items-start gap-2 mb-4 cursor-pointer">
              {/* eslint-disable-next-line no-restricted-syntax -- native checkbox: no ui/Checkbox primitive exists yet */}
              <input
                type="checkbox"
                checked={respectManual}
                onChange={(e) => setRespectManual(e.target.checked)}
                className="mt-1 accent-green-700"
              />
              <div className="text-sm">
                <div className="font-medium text-ink-soft">Respect my manual picks</div>
                <div className="text-xs text-ink-faint">
                  Keep any assets you've already assigned to a side; only fill in unassigned ones.
                </div>
              </div>
            </label>

            <div className="bg-cream border border-cream-dark rounded-xl p-3 text-xs text-ink-soft mb-3">
              The optimizer reads each asset's ownership signals — account holder,
              separate-property claims, retirement employer, your manual picks — and
              proposes the division that hits the target with as little cash changing
              hands as possible: hard assets stay with whoever uses them, liquid and
              retirement accounts do the balancing. Nothing is written until you click Apply.
            </div>

            {error && (
              <div className="mb-3 px-3 py-3 rounded-lg bg-danger-soft border border-danger-accent text-sm text-danger">
                <div className="mb-2">{error}</div>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => previewMutation.mutate({ forceGreedy: true })}
                  loading={previewMutation.isPending}
                >
                  Run deterministic split instead
                </Button>
              </div>
            )}
          </>
        )}

        {preview && (
          <>
            <div className="grid grid-cols-3 gap-3 mb-1">
              <div className="bg-party-client-soft rounded-xl p-3 text-center">
                <div className="t-eyebrow truncate text-party-client" title={names.fullA}>{names.shortA}</div>
                <div className="t-num-hero text-party-client">{fmt(preview.preview_a_net)}</div>
                <div className="text-xs text-party-client">
                  {preview.percent_valid === false ? '—' : `${fmtPct1(preview.preview_a_percent)}%`}
                </div>
              </div>
              <div className="bg-cream rounded-xl p-3 text-center">
                <div className="t-eyebrow">Target</div>
                <div className="t-num-hero text-ink-soft">
                  {fmtPct1(preview.target_a_percent)}/{fmtPct1(100 - preview.target_a_percent)}
                </div>
                <div className="text-xs text-ink-faint">{fmt(preview.total_net_value)} total</div>
              </div>
              <div className="bg-party-opposing-soft rounded-xl p-3 text-center">
                <div className="t-eyebrow truncate text-party-opposing" title={names.fullB}>{names.shortB}</div>
                <div className="t-num-hero text-party-opposing">{fmt(preview.preview_b_net)}</div>
                <div className="text-xs text-party-opposing">
                  {preview.percent_valid === false ? '—' : `${fmtPct1(preview.preview_b_percent)}%`}
                </div>
              </div>
            </div>
            <div className="t-caption text-center mb-3">Side totals are after debts.</div>

            {gap !== null && (
              <div className="text-xs text-ink-soft mb-2">
                Gap to target:{' '}
                <strong
                  className={
                    Math.abs(gap) < 1 ? 'text-green-700' : Math.abs(gap) < 5 ? 'text-clay-600' : 'text-danger'
                  }
                >
                  {gap > 0 ? '+' : ''}
                  {gap.toFixed(1)} pts
                </strong>{' '}
                vs {fmtPct1(preview.target_a_percent)}% target to {names.shortA}.
              </div>
            )}
            {typeof preview.equalizing_payment === 'number' && preview.equalizing_payment > 0 && (
              <div className="text-xs text-ink-soft mb-2">
                Equalizing payment:{' '}
                <strong className="text-clay-600">{fmt(preview.equalizing_payment)}</strong>
                {preview.equalizing_payer && (
                  <> from {preview.equalizing_payer === 'SPOUSE_A' ? names.shortA : names.shortB} to reach the exact target.</>
                )}
              </div>
            )}
            {typeof preview.equalizing_payment === 'number' &&
              Math.abs(preview.total_net_value) > 0 &&
              preview.equalizing_payment > 0.05 * Math.abs(preview.total_net_value) && (
                <div className="mb-3 px-3 py-2 rounded-lg bg-clay-50 border border-clay-200 text-xs text-clay-700 leading-relaxed">
                  <strong>
                    This plan asks {preview.equalizing_payer === 'SPOUSE_A' ? names.shortA : names.shortB} to
                    write a {fmt(preview.equalizing_payment)} check
                  </strong>{' '}
                  ({((preview.equalizing_payment / Math.abs(preview.total_net_value)) * 100).toFixed(0)}%
                  of the estate) — most people can't. Consider dividing a large account instead
                  (set a retirement account to “Divide”, or switch a locked account to “Auto”)
                  so the split balances in kind.
                </div>
              )}
            {(preview.separate_assets_count ?? 0) > 0 && (
              <div className="mb-2 text-xs text-ink-soft">
                {preview.separate_assets_count} separate-property asset
                {preview.separate_assets_count === 1 ? '' : 's'} ({fmt(preview.separate_net ?? 0)})
                excluded — separate property is not divided.
              </div>
            )}
            {(preview.unplaced_net ?? 0) > 0.005 && (
              <div className="mb-2 text-xs text-clay-700">
                {fmt(preview.unplaced_net as number)} stays unplaced (unresolved separate-property
                claims the optimizer won't move).
              </div>
            )}
            {applyCount === 0 && (
              <div className="mb-3 px-3 py-2 rounded-lg bg-good-soft border border-green-300 text-xs text-green-700 leading-relaxed">
                <strong>This plan matches the current split</strong> — every asset is already
                where the optimizer would put it, so there's nothing to apply.
              </div>
            )}
            {preview.explanation && <HowItDecided explanation={preview.explanation} />}
            <div className="text-xs text-ink-faint mb-2">
              {changedCount} of {preview.suggestions.length} assets
              {liabilityCount > 0 && <> and {liabilityCount} debt{liabilityCount === 1 ? '' : 's'}</>} will change.{' '}
              {preview.fallback_used && (
                <span className="text-clay-600">AI was unavailable — used deterministic fallback.</span>
              )}
              {!preview.fallback_used && preview.ai_cost_usd > 0 && (
                <span className="text-ink-faint">AI cost: ${preview.ai_cost_usd.toFixed(4)}</span>
              )}
            </div>

            <div className="border border-cream-dark rounded-xl divide-y divide-cream-dark max-h-80 overflow-y-auto">
              {preview.suggestions.map((s) => {
                const badge = directiveBadge(s, names);
                return (
                  <div key={s.asset_id} className="px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <AssetIcon type={s.asset_type} size={12} className="text-ink-faint" />
                          <span className="font-medium text-sm text-ink truncate">{s.name}</span>
                          <span className="text-xs text-ink-faint">{fmt(s.net_value)}</span>
                          {s.recommend_sell && (
                            <Tag size="sm" tone="neutral" className="uppercase">Recommend sale</Tag>
                          )}
                          {s.entity_holder && (
                            <Tag
                              size="sm"
                              tone="neutral"
                              className="uppercase"
                              title={`Held by ${s.entity_holder} — the entity's money, not either spouse's`}
                            >
                              Entity-held
                            </Tag>
                          )}
                        </div>
                        {s.rationale && (
                          <div className="text-xs text-ink-faint mt-1 ml-6">{s.rationale}</div>
                        )}
                        {typeof s.if_kept_home_payment === 'number' && (
                          <div className="text-xs text-clay-600 mt-1 ml-6">
                            Kept with its holder instead: {fmt(s.if_kept_home_payment)} equalizing payment.
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-xs whitespace-nowrap">
                        {s.changed && (
                          <>
                            <span className="text-ink-faint">
                              {directiveLabel(s.current_directive, names)}
                            </span>
                            <ArrowRight size={12} className="text-ink-faint" />
                          </>
                        )}
                        <span className={`inline-flex items-center rounded-md px-2 py-1 font-semibold leading-none ${badge.cls}`}>
                          {badge.text}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {liabilityCount > 0 && (
              <div className="mt-2 text-xs text-ink-soft">
                Debts placed by this plan:{' '}
                {(preview.liability_placements ?? [])
                  .map(
                    (l) =>
                      `${l.name ?? 'debt'} → ${l.side === 'SPOUSE_A' ? names.shortA : names.shortB}`,
                  )
                  .join('; ')}
                .
              </div>
            )}

            {error && (
              <div className="mt-3 px-3 py-2 rounded-lg bg-danger-soft border border-danger-accent text-sm text-danger">
                {error}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

/* ============================ MAIN TAB ============================ */

export default function NetValueSplitTab({ caseId, caseData }: { caseId: string; caseData?: Case }) {
  const partyNames = derivePartyNames(caseData);
  const { data, isLoading, error } = useQuery<SplitResponse>({
    queryKey: ['net-value-split', caseId],
    queryFn: async () => {
      const res = await api.get(`/cases/${caseId}/net-value-split`);
      return res.data;
    },
  });

  const [showAssumptions, setShowAssumptions] = useState(false);
  const [showOptimize, setShowOptimize] = useState(false);

  const queryClient = useQueryClient();
  const retype = useMutation({
    mutationFn: () => api.post(`/cases/${caseId}/retype-accounts`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['net-value-split', caseId] });
      queryClient.invalidateQueries({ queryKey: ['assets', caseId] });
      queryClient.invalidateQueries({ queryKey: ['liabilities', caseId] });
    },
  });

  if (isLoading) {
    return (
      <div role="status" aria-label="Calculating net values" className="space-y-4">
        <Skeleton variant="rect" height={160} className="rounded-lg" />
        <div className="grid grid-cols-2 gap-4">
          <Skeleton variant="rect" height={280} className="rounded-lg" />
          <Skeleton variant="rect" height={280} className="rounded-lg" />
        </div>
        <span className="sr-only">Calculating net values…</span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-12">
        <p className="text-danger">Couldn't load the net value split.</p>
        <p className="text-sm text-ink-faint mt-1">
          Check that the case has assets, then reload.
        </p>
      </div>
    );
  }

  const { assets, separate_assets: separateAssets, liabilities = [], warnings = [] } = data;

  const splitAssets = assets.filter((a) => a.split_mode === 'percent_split');
  const sideAssets = (side: 'SPOUSE_A' | 'SPOUSE_B') =>
    assets.filter((a) => a.assigned_to === side && a.split_mode !== 'percent_split');
  const trayAssets = assets.filter(
    (a) => a.assigned_to !== 'SPOUSE_A' && a.assigned_to !== 'SPOUSE_B' && a.split_mode !== 'percent_split',
  );

  const byClass = (rows: NetValueAsset[]): Record<AssetClass, NetValueAsset[]> => ({
    liquid: rows.filter((a) => classOf(a.net_type) === 'liquid'),
    retirement: rows.filter((a) => classOf(a.net_type) === 'retirement'),
    hard: rows.filter((a) => classOf(a.net_type) === 'hard'),
  });

  const aLiabilities = liabilities.filter((l) => l.assigned_to === 'SPOUSE_A');
  const bLiabilities = liabilities.filter((l) => l.assigned_to === 'SPOUSE_B');
  const trayLiabilities = liabilities.filter(
    (l) => l.assigned_to !== 'SPOUSE_A' && l.assigned_to !== 'SPOUSE_B',
  );
  const unmatched = data.unmatched_account_balances ?? [];
  const trayCount = trayAssets.length + trayLiabilities.length + unmatched.length;
  // The undecided figure counts EVERYTHING on the table, unmatched statement
  // balances included — dollars are the point, not item counts. A credit-card
  // statement is money owed (negative); a credit balance on one is money held.
  const trayNet =
    trayAssets.reduce((s, a) => s + a.net_value, 0) -
    trayLiabilities.reduce((s, l) => s + l.balance, 0) +
    unmatched.reduce((s, u) => s + (u.is_credit_card ? -(u.balance || 0) : u.balance || 0), 0);

  if (assets.length === 0 && separateAssets.length === 0 && liabilities.length === 0 && unmatched.length === 0) {
    return (
      <EmptyState
        icon={Scale}
        title="Nothing to divide yet"
        description="Add assets in the Assets tab, or upload statements to get started."
      />
    );
  }

  return (
    <PartyNamesContext.Provider value={partyNames}>
      {showAssumptions && (
        <AssumptionsModal
          caseId={caseId}
          assumptions={data.assumptions}
          onClose={() => setShowAssumptions(false)}
        />
      )}
      {showOptimize && (
        <AIOptimizeModal
          caseId={caseId}
          defaultTarget={data.target_split_percent}
          onClose={() => setShowOptimize(false)}
        />
      )}

      <BalanceBar
        data={data}
        caseId={caseId}
        onOpenAssumptions={() => setShowAssumptions(true)}
        onOpenOptimize={() => setShowOptimize(true)}
      />

      {warnings.map((w, i) => (
        <div
          key={`${w.kind}-${i}`}
          className="mb-3 px-4 py-3 rounded-lg bg-clay-50 border border-clay-200 text-sm text-clay-700"
        >
          {w.text}
        </div>
      ))}

      <div className={`grid gap-4 ${trayCount > 0 ? 'grid-cols-1 lg:grid-cols-[1fr_minmax(220px,260px)_1fr]' : 'grid-cols-1 lg:grid-cols-2'}`}>
        <SideLane
          side="a"
          assetsByClass={byClass(sideAssets('SPOUSE_A'))}
          liabs={aLiabilities}
          allAssets={assets}
          caseId={caseId}
        />
        {trayCount > 0 && (
          <section
            aria-label="On the table"
            className="rounded-xl border border-border-subtle bg-cream overflow-hidden self-start"
          >
            <div className="px-3 py-2 border-b border-border-subtle">
              <div className="t-card text-ink">On the table</div>
              <div className="t-caption">
                {trayCount} item{trayCount === 1 ? '' : 's'}
                {Math.abs(trayNet) >= 1 && <> · {fmt(trayNet)} undecided</>} — assign a side, or
                let Optimize place them.
              </div>
            </div>
            {trayAssets.map((a) => (
              <TableAssetRow key={`asset-${a.id}`} asset={a} caseId={caseId} />
            ))}
            {trayLiabilities.map((l) => (
              <TableLiabilityRow key={`liab-${l.id}`} liability={l} caseId={caseId} />
            ))}
            {unmatched.map((acct) => (
              <TableCreateRow key={`acct-${acct.bank_label}`} account={acct} caseId={caseId} />
            ))}
          </section>
        )}
        <SideLane
          side="b"
          assetsByClass={byClass(sideAssets('SPOUSE_B'))}
          liabs={bLiabilities}
          allAssets={assets}
          caseId={caseId}
        />
      </div>

      {splitAssets.length > 0 && (
        <section
          aria-label="Divided assets"
          className="mt-4 rounded-xl border border-border-subtle bg-cream-light overflow-hidden"
        >
          <div className="px-3 py-2 bg-surface-well border-b border-border-subtle flex items-center justify-between">
            <span className="t-eyebrow">Divided between both sides</span>
            <span className="t-num text-xs text-ink-soft">
              {fmt(splitAssets.reduce((s, a) => s + a.net_value, 0))}
            </span>
          </div>
          {splitAssets.map((a) => (
            <DividedAssetRow key={a.id} asset={a} caseId={caseId} />
          ))}
        </section>
      )}

      {separateAssets.length > 0 && (
        <section
          aria-label="Separate property"
          className="mt-4 rounded-xl border border-border-subtle bg-cream overflow-hidden"
        >
          <div className="px-3 py-2 border-b border-border-subtle flex items-center justify-between">
            <div>
              <span className="t-eyebrow">Separate property — not divided</span>
              <div className="t-caption">
                Owned before marriage or received by gift/inheritance; confirmatory only.
              </div>
            </div>
            <span className="t-num text-xs text-ink-soft">{fmt(data.separate_net)}</span>
          </div>
          {separateAssets.map((a) => (
            <div key={a.id} className="px-3 py-2 border-b border-border-subtle last:border-0 flex items-center gap-2">
              <AssetIcon type={a.asset_type} size={16} className="text-ink-faint flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm font-medium text-ink truncate">{a.name}</span>
                  <Tag size="sm" tone="separate">Separate</Tag>
                </div>
                <div className="t-caption truncate">{a.asset_type}</div>
              </div>
              <div className="t-num text-sm font-semibold text-ink flex-shrink-0">{fmt(a.net_value)}</div>
            </div>
          ))}
        </section>
      )}

      <div className="mt-4 flex justify-end">
        {/* Account typing is corrected automatically after each upload; this is
            a quiet manual re-check for older cases. */}
        <Button
          variant="ghost"
          size="sm"
          icon={Wrench}
          onClick={() => retype.mutate()}
          disabled={retype.isPending}
          title="Recheck account types from statements (normally automatic after each upload)"
          className="text-xs font-normal text-ink-faint"
        >
          {retype.isPending ? 'Rechecking…' : 'Recheck types'}
        </Button>
      </div>
    </PartyNamesContext.Provider>
  );
}
